// Where a saved item came from, decided from the item alone with no network.
//
// The mobile Library sorts everything into these buckets: the search bar's
// filter chips, the "By source" collections in Spaces and the badge in the
// corner of each tile all read from here. Pure: safe to import anywhere.

import type { Item } from '@/stores/canvasStore';
import { parseSocialUrl } from '@/lib/socialLinks';

export type LibrarySource =
  | 'instagram_reel'
  | 'instagram_post'
  | 'tiktok'
  | 'youtube'
  | 'x'
  | 'pinterest'
  | 'threads'
  | 'linkedin'
  | 'facebook'
  | 'reddit'
  | 'vimeo'
  | 'profile'
  | 'product'
  | 'web'
  | 'image'
  | 'video'
  | 'pdf'
  | 'file'
  | 'note';

export interface LibrarySourceMeta {
  key: LibrarySource;
  /** Plural, for chips and collection names: "Instagram Reels". */
  label: string;
  /** Singular, for a tile's accessible name: "Instagram reel". */
  noun: string;
}

/** Display order: social first (what people save most), then the web, then their own uploads and notes. */
export const LIBRARY_SOURCES: LibrarySourceMeta[] = [
  { key: 'instagram_reel', label: 'Instagram Reels', noun: 'Instagram reel' },
  { key: 'instagram_post', label: 'Instagram Posts', noun: 'Instagram post' },
  { key: 'tiktok', label: 'TikTok', noun: 'TikTok video' },
  { key: 'x', label: 'X Bookmarks', noun: 'X post' },
  { key: 'youtube', label: 'YouTube', noun: 'YouTube video' },
  { key: 'pinterest', label: 'Pinterest', noun: 'Pin' },
  { key: 'threads', label: 'Threads', noun: 'Threads post' },
  { key: 'linkedin', label: 'LinkedIn', noun: 'LinkedIn post' },
  { key: 'facebook', label: 'Facebook', noun: 'Facebook post' },
  { key: 'reddit', label: 'Reddit', noun: 'Reddit post' },
  { key: 'vimeo', label: 'Vimeo', noun: 'Vimeo video' },
  { key: 'profile', label: 'Profiles', noun: 'Profile' },
  { key: 'product', label: 'Products', noun: 'Product' },
  { key: 'web', label: 'Web Pages', noun: 'Web page' },
  { key: 'image', label: 'Images', noun: 'Image' },
  { key: 'video', label: 'Videos', noun: 'Video' },
  { key: 'pdf', label: 'PDFs', noun: 'PDF' },
  { key: 'file', label: 'Files', noun: 'File' },
  { key: 'note', label: 'Notes', noun: 'Note' },
];

const META = new Map(LIBRARY_SOURCES.map((m) => [m.key, m]));

export function sourceMeta(source: LibrarySource): LibrarySourceMeta {
  return META.get(source)!;
}

export function sourceOrder(source: LibrarySource): number {
  return LIBRARY_SOURCES.findIndex((m) => m.key === source);
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

const VIDEO_KINDS = new Set(['reel', 'video', 'short']);

/** Platform + kind from a URL, or from a stored platform name when the URL says nothing. */
function socialSource(url: string, storedPlatform: string, isVideo: boolean): LibrarySource | null {
  const info = parseSocialUrl(url);
  const platform = info?.platform ?? (storedPlatform.toLowerCase() || null);
  if (!platform) return null;
  if (info && (info.kind === 'profile' || info.kind === 'board')) {
    // A Pinterest board is a collection of pins, so it stays with Pinterest
    return info.kind === 'board' ? 'pinterest' : 'profile';
  }
  switch (platform) {
    case 'instagram':
      return isVideo || (info && VIDEO_KINDS.has(info.kind)) ? 'instagram_reel' : 'instagram_post';
    case 'tiktok':
    case 'youtube':
    case 'x':
    case 'pinterest':
    case 'threads':
    case 'linkedin':
    case 'facebook':
    case 'reddit':
    case 'vimeo':
      return platform;
    case 'twitter':
      return 'x';
    default:
      return null;
  }
}

/**
 * The bucket an item belongs in, or null for things the Library does not
 * show (folders, synced mirrors of other items).
 */
export function classifySource(item: Item): LibrarySource | null {
  const c = (item.content ?? {}) as Record<string, unknown>;
  switch (item.item_type) {
    case 'sticky':
    case 'text_card':
    case 'document':
      return 'note';
    case 'image':
      return 'image';
    case 'video':
      return 'video';
    case 'file':
      return str(c.mimeType) === 'application/pdf' || str(c.filename).toLowerCase().endsWith('.pdf') ? 'pdf' : 'file';
    case 'scraped_creative': {
      const isVideo = !!c.video_url || /video|reel/i.test(str(c.content_type));
      return socialSource(str(c.content_url) || str(c.source_url), str(c.platform), isVideo) ?? 'web';
    }
    case 'link': {
      if (c.tweet_id) return 'x';
      const url = str(c.url);
      const social = socialSource(url, str(c.platform), str(c.mediaType) === 'video');
      if (social) return social;
      if (str(c.contentType) === 'product') return 'product';
      return 'web';
    }
    default:
      return null;
  }
}

/**
 * A note that lives on a canvas board (a text card, sticky or document made
 * while working there, often by Partnr) rather than one written to your
 * mind. Everything leaves these out so the feed stays what you chose to keep;
 * they still show inside their board's space and in search results.
 *
 * Notes written in the Library carry `libraryNote: true`; older Wall notes
 * have no board. Both count as captured, wherever they are moved later.
 */
export function isCanvasNote(item: Item): boolean {
  if (item.item_type !== 'sticky' && item.item_type !== 'text_card' && item.item_type !== 'document') return false;
  const c = (item.content ?? {}) as Record<string, unknown>;
  if (c.libraryNote === true) return false;
  return !!item.board_id;
}
